import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Table, ButtonVariants } from '@carboledger/ui';
import { Plus } from '@carboledger/ui/icons';
import { useTranslations, UnitTypeContext, useUnitTypeHeader } from '@/hooks';
import {
  SummaryTiles,
  LinkShipmentsForOrder,
  buildOrderLinkFields,
  DiscrepancyModal,
} from '@/components/common';
import { buildTableLabels } from '@/config/tableLabels';
import { ROUTES } from '@/app/routes';
import emptyBoxIllustration from '@/assets/illustrations/empty-box.png';
import { useGetOrderLinkedShipmentsQuery } from '../api/ordersApi';
import { buildOrderLinkedShipmentsColumns } from '../constants/orderLinkedShipmentsColumns';
import { buildLinkedShipmentDiscrepancySections } from '../constants/orderDiscrepancy';
import { buildUnitHeaderOptions } from '../constants/orderUnits';
import { EMPTY_LIST } from '@/lib/utils';

const getShipmentLinkId = (row) => row.id;

/**
 * Props:
 *   module  route module segment, for the link wizard's save
 *   order   the order detail view model — its id, quantities and the wizard's match fields
 */
export const LinkedOutgoingShipmentTabView = ({ module, order }) => {
  const t = useTranslations();
  const navigate = useNavigate();
  const orderId = order?.id;
  const summary = order?.linkedShipmentsSummary;
  const declarationUnit = order?.declarationUnit;
  const [isLinkOpen, setIsLinkOpen] = useState(false);
  const { data: rows = EMPTY_LIST, isFetching } = useGetOrderLinkedShipmentsQuery({ id: orderId });

  const { contextValue } = useUnitTypeHeader(rows, buildUnitHeaderOptions, t);

  // A row is an outgoing shipment, so both actions land on that slice's own routes.
  const handleFulfillShipment = useCallback(
    (row) => navigate(ROUTES.outgoingShipmentFulfill(module, row.id)),
    [navigate, module],
  );
  const handleViewShipment = useCallback(
    (row) => navigate(ROUTES.outgoingShipmentDetail(module, row.id)),
    [navigate, module],
  );

  const handleLinkShipments = useCallback(() => setIsLinkOpen(true), []);
  const handleCloseLink = useCallback(() => setIsLinkOpen(false), []);

  // Ten rows off the order itself — Figma 4196:111461 / 4196:113122.
  const orderList = useMemo(() => (order ? [order] : EMPTY_LIST), [order]);

  const linkDetailFields = useCallback(
    (unitType, assignedInUnit) => buildOrderLinkFields(t, order, unitType, assignedInUnit),
    [t, order],
  );

  const [discrepancyRow, setDiscrepancyRow] = useState(null);
  const handleOpenDiscrepancy = useCallback((row) => setDiscrepancyRow(row), []);
  const handleCloseDiscrepancy = useCallback(() => setDiscrepancyRow(null), []);

  const discrepancySections = useMemo(
    () => buildLinkedShipmentDiscrepancySections(t, discrepancyRow),
    [t, discrepancyRow],
  );

  const columns = useMemo(
    () => buildOrderLinkedShipmentsColumns(t, { onOpenDiscrepancy: handleOpenDiscrepancy }),
    [t, handleOpenDiscrepancy],
  );

  const rowActions = useMemo(
    () => [
      {
        id: 'fulfill',
        label: t('ACTION_FULFILL'),
        variant: ButtonVariants.tertiary,
        onClick: handleFulfillShipment,
      },
    ],
    [t, handleFulfillShipment],
  );

  const rowMenuActions = useMemo(
    () => [{ id: 'view', label: t('COMMON_VIEW'), onClick: handleViewShipment }],
    [t, handleViewShipment],
  );

  const toolbarActions = useMemo(
    () => [
      {
        id: 'link-shipments',
        label: t('ORDERS_DETAIL_SHIPMENTS_EMPTY_CTA'),
        icon: Plus,
        variant: ButtonVariants.primary,
        onClick: handleLinkShipments,
      },
    ],
    [t, handleLinkShipments],
  );

  const tableLabels = useMemo(() => buildTableLabels(t), [t]);

  const tiles = useMemo(() => {
    if (!summary) return [];
    return [
      { label: t('ORDERS_DETAIL_TOTAL_SHIPPED_QUANTITY'), value: summary.shippedQuantity },
      {
        label: t('ORDERS_DETAIL_REMAINING_TO_SHIP_IN_UNIT', { unit: declarationUnit }),
        value: summary.remainingToShipQuantity,
      },
    ];
  }, [summary, declarationUnit, t]);

  // Table falls back to the primary toolbarAction's own label/icon/onClick when these are omitted — left explicit here for clarity.
  const emptyState = useMemo(
    () => ({
      image: <img src={emptyBoxIllustration} alt="" width={200} height={200} />,
      title: t('ORDERS_DETAIL_SHIPMENTS_EMPTY_TITLE'),
      description: t('ORDERS_DETAIL_SHIPMENTS_EMPTY_DESCRIPTION'),
    }),
    [t],
  );

  return (
    <>
      <SummaryTiles tiles={tiles}>
        <UnitTypeContext.Provider value={contextValue}>
          <Table
            columns={columns}
            data={rows}
            loading={isFetching}
            getRowId={getShipmentLinkId}
            searchable
            labels={tableLabels}
            toolbarActions={toolbarActions}
            rowActions={rowActions}
            rowMenuActions={rowMenuActions}
            actionsHeader={t('FIELD_ACTION')}
            emptyState={emptyState}
          />
        </UnitTypeContext.Provider>
      </SummaryTiles>

      <LinkShipmentsForOrder
        module={module}
        orders={orderList}
        detailFields={linkDetailFields}
        open={isLinkOpen}
        onClose={handleCloseLink}
      />

      <DiscrepancyModal
        open={discrepancyRow != null}
        onClose={handleCloseDiscrepancy}
        referenceId={discrepancyRow?.shipmentId}
        message={t('OUTGOING_SHIPMENTS_DETAIL_DISCREPANCY_MESSAGE')}
        sections={discrepancySections}
      />
    </>
  );
};
